import { useState } from "react";
import { CheckCircle2, Copy, Download, ExternalLink, FileText, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile, fmtDateTime, saveBlob } from "@/lib/api";
import { useWorkspace, useWsQuery } from "@/context/WorkspaceContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PLATFORMS = [
  ["meta ads", "Meta Ads Manager", "https://adsmanager.facebook.com/"], ["facebook", "Facebook", "https://www.facebook.com/"],
  ["instagram", "Instagram", "https://www.instagram.com/"], ["linkedin", "LinkedIn", "https://www.linkedin.com/feed/"],
  ["twitter", "X", "https://x.com/compose/post"], ["tiktok", "TikTok", "https://www.tiktok.com/upload"], ["youtube", "YouTube Studio", "https://studio.youtube.com/"],
  ["pinterest", "Pinterest", "https://www.pinterest.com/pin-creation-tool/"], ["reddit", "Reddit", "https://www.reddit.com/submit"],
  ["search", "Google Ads", "https://ads.google.com/"], ["google", "Google Ads", "https://ads.google.com/"],
];
export const platformFor = (channel) => {
  const ch = (channel || "").toLowerCase();
  if (ch === "x" || ch.startsWith("x ")) return ["X", "https://x.com/compose/post"];
  const hit = PLATFORMS.find(([k]) => ch.includes(k));
  return hit ? [hit[1], hit[2]] : null;
};
const slug = (s) => (s || "content").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 50);

function briefText(item) {
  return [`${item.title}`, `Platform: ${item.channel || "—"} · Type: ${item.format || "—"} · Campaign: ${item.campaign_name || "—"}`, `Status: approved (v${item.version || 1})`,
    "", "--- APPROVED COPY / SCRIPT ---", item.body || "", item.rationale ? `\n--- WHY THIS DRAFT ---\n${item.rationale}` : "",
    item.claims_needing_evidence?.length ? `\n--- CLAIMS NEEDING EVIDENCE ---\n${item.claims_needing_evidence.map((c) => `- ${c}`).join("\n")}` : "",
    "\nPublish manually on the platform, then record it in CRESQEN with 'Mark as manually published'. CRESQEN does not publish automatically."].join("\n");
}

function CreativeDownloads({ ids }) {
  const { path } = useWorkspace();
  const q = useWsQuery("/r/brand_assets", { enabled: ids.length > 0 });
  const assets = (q.data || []).filter((a) => ids.includes(a.id));
  if (!ids.length) return <span className="text-xs text-[#7C8BAD]" data-testid="publish-no-creative">No creative attached</span>;
  const get = async (a) => {
    if (!a.storage_path) { window.open(a.url, "_blank", "noopener,noreferrer"); return; }
    try { await downloadFile(path(`/assets/${a.id}/file?download=true`), a.original_filename); } catch (e) { toast.error(apiError(e)); }
  };
  return assets.map((a) => (
    <button key={a.id} data-testid={`publish-download-creative-${a.id}`} onClick={() => get(a)} className="cq-btn cq-btn-ghost !py-1 !px-2.5 !text-xs" title={a.approved ? "Approved creative" : "Not approved for use"}>
      <Download size={12} /> {a.name}{!a.approved && " (not approved)"}
    </button>));
}

function MarkDialog({ item, open, onOpenChange, onDone }) {
  const { path } = useWorkspace();
  const prev = item.manual_publication;
  const local = (iso) => { const d = iso ? new Date(iso) : new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };
  const [when, setWhen] = useState(local(prev?.published_at));
  const [url, setUrl] = useState(prev?.public_url || "");
  const [note, setNote] = useState(prev?.note || "");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post(path(`/content/${item.id}/manual-publication`), { published_at: new Date(when).toISOString(), public_url: url.trim() || null, note: note.trim() || null, confirm_manual: ok });
      toast.success(prev ? "Publication record corrected" : "Recorded as manually published"); onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manual-publish-dialog" className="bg-[#0A1024] border-[#243153] text-[#F7FAFF] max-w-md">
        <DialogHeader><DialogTitle className="font-display">{prev ? "Correct publication record" : "Mark as manually published"}</DialogTitle></DialogHeader>
        <label className="block text-xs text-[#AAB6CC]">Actual publication date and time
          <input type="datetime-local" data-testid="manual-publish-time" value={when} max={local()} onChange={(e) => setWhen(e.target.value)} className="cq-input mt-1 w-full" /></label>
        <label className="block text-xs text-[#AAB6CC]">Public post URL (optional)
          <input data-testid="manual-publish-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" className="cq-input mt-1 w-full" /></label>
        <label className="block text-xs text-[#AAB6CC]">Note (optional)
          <input data-testid="manual-publish-note" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} className="cq-input mt-1 w-full" /></label>
        <label className="flex gap-2 text-xs text-[#D5DDEB]"><input type="checkbox" data-testid="manual-publish-confirm" checked={ok} onChange={(e) => setOk(e.target.checked)} className="accent-[#22E6F2] mt-0.5" />
          I confirm a person published this outside CRESQEN. CRESQEN did not publish it.</label>
        <div className="flex justify-end gap-2">
          <button onClick={() => onOpenChange(false)} className="cq-btn cq-btn-ghost">Cancel</button>
          <button data-testid="manual-publish-save" disabled={!ok || !when || busy} onClick={save} className="cq-btn cq-btn-primary">{busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save record</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ItemHistory({ id }) {
  const q = useWsQuery(`/content/${id}/history`);
  if (!q.data) return null;
  const rows = [...q.data.approvals.map((a) => ({ id: a.id, at: a.reviewed_at || a.created_at, text: `${a.requested_action}: ${a.status.replace(/_/g, " ")}${a.reviewer_name ? ` by ${a.reviewer_name}` : ""}` })),
    ...q.data.activity.map((a) => ({ id: a.id, at: a.created_at, text: `${a.summary} · ${a.actor_name}` }))].sort((a, b) => (a.at < b.at ? 1 : -1));
  return (
    <div data-testid="content-history"><div className="cq-muted-label mb-1.5 flex items-center gap-1.5"><History size={12} /> Approval and activity history</div>
      <ul className="space-y-1 max-h-40 overflow-y-auto">{rows.map((r) => <li key={r.id} className="text-[11.5px] text-[#AAB6CC]"><span className="text-[#7C8BAD] font-mono">{fmtDateTime(r.at)}</span> · {r.text}</li>)}</ul></div>
  );
}

export default function ManualPublishPanel({ item }) {
  const { can, refresh } = useWorkspace();
  const [mark, setMark] = useState(false);
  const plat = platformFor(item.channel);
  const mp = item.manual_publication;
  const copy = () => navigator.clipboard.writeText(item.body || "").then(() => toast.success("Post text copied"), () => toast.error("Couldn't copy. Select the text and copy it manually."));
  const brief = () => saveBlob(new Blob([briefText(item)], { type: "text/plain;charset=utf-8" }), `${slug(item.title)}-brief.txt`);
  return (
    <section data-testid="manual-publish-panel" className="p-4 rounded-lg border border-[#4BE3A4]/25 bg-[#4BE3A4]/5 space-y-3">
      <div className="flex flex-wrap items-center gap-2"><span className="font-display text-sm text-[#F7FAFF]">Assisted publishing</span>
        <span className="text-[11px] text-[#7C8BAD]">You publish on the platform; CRESQEN records it. Nothing is posted automatically.</span></div>
      {mp && (
        <div data-testid="manual-publication-record" className="text-xs text-[#D5DDEB] space-y-0.5">
          <div><CheckCircle2 size={12} className="inline mr-1 text-[#4BE3A4]" />Manually published {fmtDateTime(mp.published_at)} · recorded by {mp.marked_by_name} on {fmtDateTime(mp.marked_at)}</div>
          {mp.public_url && <a data-testid="manual-publication-url" href={mp.public_url} target="_blank" rel="noopener noreferrer" className="text-[#22E6F2] hover:underline break-all">{mp.public_url}</a>}
          {mp.note && <div className="text-[#7C8BAD]">{mp.note}</div>}
        </div>)}
      <div className="flex flex-wrap gap-2">
        <button data-testid="publish-copy-text" onClick={copy} className="cq-btn cq-btn-ghost !py-1 !px-2.5 !text-xs"><Copy size={12} /> Copy post text</button>
        <button data-testid="publish-download-brief" onClick={brief} className="cq-btn cq-btn-ghost !py-1 !px-2.5 !text-xs"><FileText size={12} /> Download script / brief</button>
        <CreativeDownloads ids={item.asset_ids || []} />
        {plat ? <a data-testid="publish-open-platform" href={plat[1]} target="_blank" rel="noopener noreferrer" className="cq-btn cq-btn-ghost !py-1 !px-2.5 !text-xs"><ExternalLink size={12} /> Open {plat[0]}</a>
          : <span className="text-[11px] text-[#7C8BAD] self-center" data-testid="publish-no-platform">Open your {item.channel || "publishing"} tool to post this</span>}
        {can("publish_manual") && <button data-testid="publish-mark-button" onClick={() => setMark(true)} className="cq-btn cq-btn-primary !py-1 !px-2.5 !text-xs"><CheckCircle2 size={12} /> {mp ? "Correct record" : "Mark as manually published"}</button>}
      </div>
      <ItemHistory id={item.id} />
      {mark && <MarkDialog item={item} open={mark} onOpenChange={setMark} onDone={refresh} />}
    </section>
  );
}
